import React, { useEffect, useRef, useState } from 'react';
import type { Organization } from '@types';
import type { Region } from '@common/adminAccess';
import { getOrganizationDisplayName } from '@common/organizationDisplayName';
import { subscriptionStatusLabel } from '../utils/subscriptionStatusLabel';
import { loadGoogleMaps } from '../../services/googleMapsLoader';

const MAP_ID = import.meta.env.VITE_GOOGLE_MAPS_MAP_ID;

const REGION_BOUNDS: Record<Region, google.maps.LatLngBoundsLiteral> = {
    'North America': { south: 7, west: -168, north: 84, east: -52 },
    'South America': { south: -56, west: -82, north: 13, east: -34 },
    Oceania: { south: -50, west: 110, north: 15, east: 180 },
    Africa: { south: -36, west: -20, north: 38, east: 55 },
    'Middle East': { south: 12, west: 25, north: 42, east: 65 },
    Europe: { south: 34, west: -25, north: 72, east: 45 },
    Asia: { south: -10, west: 25, north: 80, east: 180 },
};

const REGION_VIEWS: Record<Region, { center: google.maps.LatLngLiteral; zoom: number }> = {
    'North America': { center: { lat: 42, lng: -105 }, zoom: 3.2 },
    'South America': { center: { lat: -21, lng: -60 }, zoom: 3.2 },
    Oceania: { center: { lat: -20, lng: 145 }, zoom: 3.3 },
    Africa: { center: { lat: 2, lng: 18 }, zoom: 3.5 },
    'Middle East': { center: { lat: 28, lng: 45 }, zoom: 4.4 },
    Europe: { center: { lat: 52, lng: 15 }, zoom: 4.1 },
    Asia: { center: { lat: 40, lng: 100 }, zoom: 3.1 },
};

interface OrganizationMapProps {
    organizations: Organization[];
    regionalAdminRegions: Region[];
    onEdit: (organization: Organization) => void;
}

function organizationPosition(organization: Organization) {
    const latitude = Number(organization.headquartersCoordinates?.latitude);
    const longitude = Number(organization.headquartersCoordinates?.longitude);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude))
        return null;
    if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180)
        return null;
    if (latitude === 0 && longitude === 0)
        return null;
    return { lat: latitude, lng: longitude };
}

function organizationAddress(organization: Organization) {
    const address = organization.headquartersAddress;
    if (!address)
        return 'Headquarters address unavailable';
    return [address.addressLine1, address.addressLine2, address.city, address.administrativeArea, address.postalCode, address.country]
        .map(value => String(value || '').trim())
        .filter(Boolean)
        .join(', ') || 'Headquarters address unavailable';
}

function additionalLocationPosition(location: NonNullable<Organization['additionalLocations']>[number]) {
    const latitude = Number(location.coordinates?.latitude);
    const longitude = Number(location.coordinates?.longitude);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude) || latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180 || latitude === 0 && longitude === 0)
        return null;
    return { lat: latitude, lng: longitude };
}

function additionalLocationAddress(location: NonNullable<Organization['additionalLocations']>[number]) {
    return [location.addressLine1, location.addressLine2, location.city, location.administrativeArea, location.postalCode, location.country]
        .map(value => String(value || '').trim())
        .filter(Boolean)
        .join(', ');
}

const OrganizationMap: React.FC<OrganizationMapProps> = ({ organizations, regionalAdminRegions, onEdit }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const onEditRef = useRef(onEdit);
    const [error, setError] = useState<string | null>(null);
    const [selectedMarker, setSelectedMarker] = useState<{ organizationId: string; address: string; label: string } | null>(null);
    const mappedCount = organizations.filter(organizationPosition).length;
    const additionalMappedCount = organizations.reduce((count, organization) => count + (organization.additionalLocations || []).filter(additionalLocationPosition).length, 0);
    const regionalAdminRegionKey = regionalAdminRegions.join('|');
    const selectedOrganization = organizations.find(organization => organization.id === selectedMarker?.organizationId) || null;

    useEffect(() => {
        onEditRef.current = onEdit;
    }, [onEdit]);

    useEffect(() => {
        if (!containerRef.current)
            return;
        let disposed = false;
        let markers: google.maps.marker.AdvancedMarkerElement[] = [];
        setError(null);

        const initialize = async () => {
            try {
                await loadGoogleMaps();
                const { Map } = await google.maps.importLibrary('maps') as google.maps.MapsLibrary;
                const { AdvancedMarkerElement } = await google.maps.importLibrary('marker') as google.maps.MarkerLibrary;
                if (disposed || !containerRef.current)
                    return;

                const initialView = regionalAdminRegions.length === 1 ? REGION_VIEWS[regionalAdminRegions[0]] : null;
                const map = new Map(containerRef.current, {
                    center: initialView?.center || { lat: 20, lng: 0 },
                    zoom: initialView?.zoom || 2,
                    mapId: MAP_ID,
                    clickableIcons: false,
                    fullscreenControl: true,
                    mapTypeControl: false,
                    streetViewControl: false,
                    gestureHandling: 'cooperative',
                });

                for (let index = 0; index < organizations.length; index++) {
                    const organization = organizations[index];
                    const position = organizationPosition(organization);
                    if (position) {
                        const markerButton = document.createElement('button');
                        markerButton.type = 'button';
                        markerButton.setAttribute('aria-label', `View ${getOrganizationDisplayName(organization)}`);
                        markerButton.style.width = '30px';
                        markerButton.style.height = '30px';
                        markerButton.style.border = '2px solid white';
                        markerButton.style.borderRadius = '50% 50% 50% 0';
                        markerButton.style.background = '#2563eb';
                        markerButton.style.boxShadow = '0 3px 8px rgba(15, 23, 42, 0.35)';
                        markerButton.style.cursor = 'pointer';
                        markerButton.style.transform = 'rotate(-45deg)';
                        markerButton.addEventListener('click', event => {
                            event.stopPropagation();
                            setSelectedMarker({ organizationId: organization.id, address: organizationAddress(organization), label: 'Headquarters' });
                        });
                        const marker = new AdvancedMarkerElement({ position, title: getOrganizationDisplayName(organization) });
                        marker.append(markerButton);
                        markers.push(marker);
                        marker.map = map;
                    }

                    const additionalLocations = organization.additionalLocations || [];
                    for (let locationIndex = 0; locationIndex < additionalLocations.length; locationIndex++) {
                        const location = additionalLocations[locationIndex];
                        const locationPosition = additionalLocationPosition(location);
                        if (!locationPosition)
                            continue;
                        const locationButton = document.createElement('button');
                        locationButton.type = 'button';
                        locationButton.setAttribute('aria-label', `View ${getOrganizationDisplayName(organization)} additional location ${locationIndex + 1}`);
                        locationButton.style.width = '26px';
                        locationButton.style.height = '26px';
                        locationButton.style.border = '2px solid white';
                        locationButton.style.borderRadius = '50% 50% 50% 0';
                        locationButton.style.background = '#16a34a';
                        locationButton.style.boxShadow = '0 3px 8px rgba(15, 23, 42, 0.35)';
                        locationButton.style.cursor = 'pointer';
                        locationButton.style.transform = 'rotate(-45deg)';
                        locationButton.addEventListener('click', event => {
                            event.stopPropagation();
                            setSelectedMarker({ organizationId: organization.id, address: additionalLocationAddress(location), label: `Additional Location ${locationIndex + 1}` });
                        });
                        const locationMarker = new AdvancedMarkerElement({ position: locationPosition, title: `${getOrganizationDisplayName(organization)} — Additional Location ${locationIndex + 1}` });
                        locationMarker.append(locationButton);
                        markers.push(locationMarker);
                        locationMarker.map = map;
                    }
                }

                if (regionalAdminRegions.length > 1) {
                    const regionalBounds = new google.maps.LatLngBounds();
                    for (let index = 0; index < regionalAdminRegions.length; index++) {
                        const bounds = REGION_BOUNDS[regionalAdminRegions[index]];
                        if (!bounds)
                            continue;
                        regionalBounds.extend({ lat: bounds.south, lng: bounds.west });
                        regionalBounds.extend({ lat: bounds.north, lng: bounds.east });
                    }
                    map.fitBounds(regionalBounds, 48);
                }
            } catch (mapError) {
                console.error('Organization map failed:', mapError);
                if (!disposed)
                    setError('Organization map could not be loaded.');
            }
        };

        void initialize();
        return () => {
            disposed = true;
            for (let index = 0; index < markers.length; index++)
                markers[index].map = null;
            markers = [];
        };
    }, [organizations, regionalAdminRegionKey]);

    return (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-5 py-3">
                <p className="text-sm font-bold text-slate-700">{mappedCount} mapped organizations · {additionalMappedCount} additional locations</p>
                {mappedCount < organizations.length && <p className="text-xs text-slate-500">{organizations.length - mappedCount} organizations need headquarters coordinates.</p>}
            </div>
            <div className="relative">
                <div ref={containerRef} className="h-[32rem] w-full bg-slate-100" aria-label="Organization headquarters map" />
                {error && <div role="alert" className="absolute inset-0 z-20 flex items-center justify-center bg-red-50 text-sm font-bold text-red-700">{error}</div>}
                {selectedOrganization && (
                    <div className="absolute right-4 top-4 z-10 w-[min(20rem,calc(100%-2rem))] rounded-xl border border-slate-200 bg-white p-4 shadow-xl">
                        <div className="flex items-start justify-between gap-3">
                            <h3 className="font-extrabold text-slate-900">{getOrganizationDisplayName(selectedOrganization)}</h3>
                            <button type="button" onClick={() => setSelectedMarker(null)} className="rounded px-2 py-0.5 text-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label="Close organization details">×</button>
                        </div>
                        <p className="mt-2 text-[10px] font-black uppercase tracking-widest text-green-700">{selectedMarker?.label}</p>
                        <p className="mt-1 text-xs leading-5 text-slate-600">{selectedMarker?.address}</p>
                        <p className="mt-2 text-xs font-semibold text-slate-500">{selectedOrganization.region || 'Global'} · {subscriptionStatusLabel(selectedOrganization.subscriptionStatus, selectedOrganization.isTestOrganization, 'Inactive')}</p>
                        <button type="button" onClick={() => onEditRef.current(selectedOrganization)} className="mt-3 text-sm font-bold text-blue-600 hover:text-blue-800">Edit organization</button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default OrganizationMap;
